import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import {
  CheckCircle2,
  XCircle,
  ArrowRight,
  Table,
  PlayCircle,
  AlertCircle,
  Shield,
  FileCheck,
  RefreshCcw,
} from "lucide-react";

interface SprintActivity {
  id: string;
  sprintId: string;
  activityType: string;
  title: string;
  description: string | null;
  metadata: Record<string, any>;
  actorType: string;
  actorName: string | null;
  createdAt: string;
}

interface SprintActivityTimelineProps {
  sprintId: string;
}

const ACTIVITY_TYPE_CONFIG = {
  stage_changed: {
    icon: ArrowRight,
    color: "text-blue-600 bg-blue-100",
    label: "Stage Changed",
  },
  gate_passed: {
    icon: Shield,
    color: "text-green-600 bg-green-100",
    label: "Gate Passed",
  },
  gate_failed: {
    icon: XCircle,
    color: "text-red-600 bg-red-100",
    label: "Gate Failed",
  },
  table_added: {
    icon: Table,
    color: "text-purple-600 bg-purple-100",
    label: "Table Added",
  },
  status_changed: {
    icon: RefreshCcw,
    color: "text-orange-600 bg-orange-100",
    label: "Status Changed",
  },
  sprint_created: {
    icon: PlayCircle,
    color: "text-teal-600 bg-teal-100",
    label: "Sprint Created",
  },
  sprint_updated: {
    icon: FileCheck,
    color: "text-indigo-600 bg-indigo-100",
    label: "Sprint Updated",
  },
  test_executed: {
    icon: CheckCircle2,
    color: "text-emerald-600 bg-emerald-100",
    label: "Test Executed",
  },
  blocker_found: {
    icon: AlertCircle,
    color: "text-red-600 bg-red-100",
    label: "Blocker Found",
  },
  blocker_resolved: {
    icon: CheckCircle2,
    color: "text-green-600 bg-green-100",
    label: "Blocker Resolved",
  },
  checkpoint_created: {
    icon: FileCheck,
    color: "text-cyan-600 bg-cyan-100",
    label: "Checkpoint Created",
  },
  document_added: {
    icon: FileCheck,
    color: "text-violet-600 bg-violet-100",
    label: "Document Added",
  },
  validation_started: {
    icon: FileCheck,
    color: "text-indigo-600 bg-indigo-100",
    label: "Validation Started",
  },
  validation_passed: {
    icon: CheckCircle2,
    color: "text-emerald-600 bg-emerald-100",
    label: "Validation Passed",
  },
  validation_failed: {
    icon: XCircle,
    color: "text-red-600 bg-red-100",
    label: "Validation Failed",
  },
  gate_executed: {
    icon: Shield,
    color: "text-purple-600 bg-purple-100",
    label: "Gate Executed",
  },
};

export function SprintActivityTimeline({ sprintId }: SprintActivityTimelineProps) {
  const { data: activities = [], isLoading } = useQuery<SprintActivity[]>({
    queryKey: [`/api/project-manager/sprints/${sprintId}/activity`],
    enabled: !!sprintId,
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-3">
          <div className="text-center py-8">
            <p className="text-sm text-muted-foreground">Loading activity...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (activities.length === 0) {
    return (
      <Card>
        <CardContent className="pt-3">
          <div className="text-center py-8">
            <p className="text-sm text-muted-foreground">No activity recorded for this sprint</p>
            <p className="text-xs text-muted-foreground mt-1">
              Activity will appear here as the sprint progresses
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="pt-3">
        <div className="space-y-4">
          {activities.map((activity, index) => {
            const config =
              ACTIVITY_TYPE_CONFIG[activity.activityType as keyof typeof ACTIVITY_TYPE_CONFIG] ||
              ACTIVITY_TYPE_CONFIG.sprint_updated;
            const Icon = config.icon;

            return (
              <div key={activity.id} className="relative">
                {/* Timeline connector line */}
                {index < activities.length - 1 && (
                  <div className="absolute left-4 top-8 bottom-0 w-0.5 bg-muted" />
                )}

                {/* Activity item */}
                <div className="flex gap-3">
                  {/* Icon */}
                  <div
                    className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${config.color}`}
                  >
                    <Icon className="h-4 w-4" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="text-sm font-semibold">{activity.title}</h4>
                          <Badge variant="outline" className="text-xs">
                            {config.label}
                          </Badge>
                        </div>
                        {activity.description && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {activity.description}
                          </p>
                        )}
                      </div>
                      <time className="text-xs text-muted-foreground whitespace-nowrap">
                        {format(new Date(activity.createdAt), "MMM d, HH:mm")}
                      </time>
                    </div>

                    {/* Metadata */}
                    {activity.metadata && Object.keys(activity.metadata).length > 0 && (
                      <div className="mt-2 text-xs bg-muted/50 rounded-md p-2 space-y-1">
                        {activity.activityType === "stage_changed" && (
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground">Stage:</span>
                            <code className="bg-background px-1.5 py-0.5 rounded text-xs">
                              {activity.metadata.from_stage || "none"}
                            </code>
                            <ArrowRight className="h-3 w-3 text-muted-foreground" />
                            <code className="bg-background px-1.5 py-0.5 rounded text-xs font-medium">
                              {activity.metadata.to_stage}
                            </code>
                          </div>
                        )}
                        {activity.activityType === "gate_passed" && (
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="text-muted-foreground">Gate:</span>
                              <code className="bg-background px-1.5 py-0.5 rounded text-xs">
                                {activity.metadata.gate || `GATE-${activity.metadata.gate_number}` || "GATE-?"}
                              </code>
                            </div>
                            {activity.metadata.artifact && (
                              <div className="flex items-center gap-2">
                                <span className="text-muted-foreground">Artifact:</span>
                                <code className="bg-background px-1.5 py-0.5 rounded text-xs font-mono">
                                  {activity.metadata.artifact}
                                </code>
                              </div>
                            )}
                          </div>
                        )}
                        {activity.activityType === "table_added" && activity.metadata.table_name && (
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground">Table:</span>
                            <code className="bg-background px-1.5 py-0.5 rounded text-xs font-mono">
                              {activity.metadata.table_name}
                            </code>
                          </div>
                        )}
                        {activity.activityType === "status_changed" && (
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground">Status:</span>
                            <code className="bg-background px-1.5 py-0.5 rounded text-xs">
                              {activity.metadata.from_status || "none"}
                            </code>
                            <ArrowRight className="h-3 w-3 text-muted-foreground" />
                            <code className="bg-background px-1.5 py-0.5 rounded text-xs font-medium">
                              {activity.metadata.to_status}
                            </code>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Actor */}
                    <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                      <span>
                        {activity.actorType === "agent" ? "🤖" : "👤"}
                      </span>
                      <span>{activity.actorName || "System"}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
